<template>
  <v-card outlined class="block">
    <h3>{{ $t('hedgeOS.dashboard.payoffStructure.title') }} <span class="meta">{{ $t('hedgeOS.dashboard.payoffStructure.meta') }}</span></h3>
    <div class="chart-wrap" style="height: 280px;">
      <svg viewBox="0 0 600 280" preserveAspectRatio="none">
        <!-- grid -->
        <line class="grid-line" x1="0" y1="70" x2="600" y2="70"/>
        <line class="grid-line" x1="0" y1="140" x2="600" y2="140"/>
        <line class="grid-line" x1="0" y1="210" x2="600" y2="210"/>
        <line class="grid-line" x1="300" y1="0" x2="300" y2="240"/>
        <line x1="0" y1="140" x2="600" y2="140" stroke="#8a857a" stroke-width="1"/>

        <!-- Physical long (dashed, going up) -->
        <path fill="none" stroke="#8a857a" stroke-width="1.2" stroke-dasharray="3,3" d="M0,240 L600,40"/>
        <text class="axis-label" x="540" y="48" fill="#c9c3b3">{{ $t('hedgeOS.dashboard.payoffStructure.physical') }}</text>

        <!-- CBOT short (dashed, going down) -->
        <path fill="none" stroke="#8a857a" stroke-width="1.2" stroke-dasharray="3,3" d="M0,40 L600,240"/>
        <text class="axis-label" x="540" y="235" fill="#c9c3b3">{{ $t('hedgeOS.dashboard.payoffStructure.shortCbot') }}</text>

        <!-- Combined payoff (horizontal — hedged) -->
        <path fill="rgba(255,77,31,0.08)" d="M0,140 L600,140 L600,160 L0,160 Z"/>
        <path fill="none" stroke="#ff4d1f" stroke-width="2.5" d="M0,145 L600,145"/>

        <!-- strike marker -->
        <circle cx="300" cy="145" r="5" fill="#ff4d1f"/>
        <circle cx="300" cy="145" r="10" fill="#ff4d1f" opacity="0.3"/>
        <text class="axis-label" x="308" y="138" fill="#ff4d1f">{{ $t('hedgeOS.dashboard.payoffStructure.lockedIn') }}</text>

        <!-- axis labels -->
        <text class="axis-label" x="0" y="262">{{ $t('hedgeOS.dashboard.payoffStructure.axisDown') }}</text>
        <text class="axis-label" x="280" y="262">{{ $t('hedgeOS.dashboard.payoffStructure.axisSpot') }}</text>
        <text class="axis-label" x="555" y="262">{{ $t('hedgeOS.dashboard.payoffStructure.axisUp') }}</text>
        <text class="axis-label" x="5" y="68">+ 30/sc</text>
        <text class="axis-label" x="5" y="143" fill="#8a857a">0</text>
        <text class="axis-label" x="5" y="210">- 30/sc</text>
      </svg>
    </div>

    <h3 style="margin-top:2rem;">{{ $t('hedgeOS.dashboard.payoffStructure.bookTitle') }} <span class="meta">{{ $t('hedgeOS.dashboard.payoffStructure.bookMeta') }}</span></h3>
    <table class="mtm-table">
      <thead>
        <tr>
          <th>{{ $t('hedgeOS.dashboard.payoffStructure.headers.leg') }}</th>
          <th style="text-align:right;">{{ $t('hedgeOS.dashboard.payoffStructure.headers.px') }}</th>
          <th style="text-align:right;">{{ $t('hedgeOS.dashboard.payoffStructure.headers.mtm') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in hedgeLegs" :key="row.label">
          <td class="sym">
            {{ row.label }} <span class="tag" :class="row.side">{{ sideLabel(row.side) }}</span><br>
            <span style="color:var(--muted);font-size:0.65rem;">{{ $t(`hedgeOS.dashboard.payoffStructure.legNotes.${row.noteKey}`) }}</span>
          </td>
          <td style="text-align:right;">{{ row.px }}</td>
          <td style="text-align:right;" :class="row.mtm.startsWith('-') ? 'neg' : 'pos'">{{ row.mtm }}</td>
          <td>
            <svg width="60" height="24" viewBox="0 0 60 24">
              <path class="spark" :stroke="row.sparkColor" :d="row.sparkPath" />
            </svg>
          </td>
        </tr>
      </tbody>
    </table>

    <h3 style="margin-top:2rem;">{{ $t('hedgeOS.dashboard.payoffStructure.advisorTitle') }} <span class="meta">{{ $t('hedgeOS.dashboard.payoffStructure.advisorMeta') }}</span></h3>
    <div class="advisor-grid">
      <template v-for="rule in advisorRules">
        <div :key="rule.key + '-c'" style="color:var(--dim);">{{ $t(`hedgeOS.dashboard.payoffStructure.rules.${rule.key}.condition`) }}</div>
        <div :key="rule.key + '-a'">→ {{ $t(`hedgeOS.dashboard.payoffStructure.rules.${rule.key}.action`) }}</div>
        <div :key="rule.key + '-s'" :style="{ color: rule.statusColor }">{{ $t(`hedgeOS.dashboard.payoffStructure.status.${rule.statusKey}`) }}</div>
      </template>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "PayoffStructure",
  data() {
    return {
      hedgeLegs: [
        { label: "CBOT SOY · JUL26", side: "sell", noteKey: "cbotSoyJul", px: "1048.75", mtm: "+ US$ 218k", sparkColor: "#7bc4a4", sparkPath: "M0,6 L10,10 L20,8 L30,12 L40,14 L50,13 L60,16" },
        { label: "CBOT CORN · JUL26", side: "sell", noteKey: "cbotCornJul", px: "442.50", mtm: "+ US$ 44k", sparkColor: "#7bc4a4", sparkPath: "M0,14 L10,13 L20,15 L30,11 L40,12 L50,9 L60,10" },
        { label: "NDF USD 6M", side: "sell", noteKey: "ndfUsd6m", px: "5.8120", mtm: "- R$ 114k", sparkColor: "#ff4d1f", sparkPath: "M0,10 L10,12 L20,11 L30,15 L40,16 L50,17 L60,18" },
        { label: "LME AL · 3M", side: "buy", noteKey: "lmeAl3m", px: "2,418", mtm: "+ US$ 12k", sparkColor: "#7bc4a4", sparkPath: "M0,18 L10,15 L20,14 L30,12 L40,11 L50,9 L60,6" },
      ],
      advisorRules: [
        { key: "cbotHigh", statusKey: "armed", statusColor: "var(--cool)" },
        { key: "usdHigh", statusKey: "armed", statusColor: "var(--cool)" },
        { key: "marginLow", statusKey: "standby", statusColor: "var(--warn)" },
        { key: "basisLow", statusKey: "off", statusColor: "var(--muted)" },
      ],
    };
  },
  methods: {
    sideLabel(side) {
      return side === "sell" ? this.$t("hedgeOS.dashboard.sideSell") : this.$t("hedgeOS.dashboard.sideBuy");
    },
  },
};
</script>

<style scoped>
.block {
  background: var(--ink);
  padding: 2rem;
}

.block h3 {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 1.5rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.block h3 .meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  color: var(--muted);
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.chart-wrap {
  position: relative;
  margin-top: 0.5rem;
}

.chart-wrap svg {
  width: 100%;
  height: 100%;
}

.axis-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  fill: var(--muted);
}

.grid-line {
  stroke: var(--grid);
  stroke-width: 1;
}

.mtm-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem;
}

.mtm-table th {
  text-align: left;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.62rem;
  font-weight: 400;
  padding: 0.6rem 0.5rem;
  border-bottom: 1px solid var(--line);
}

.mtm-table td {
  padding: 0.8rem 0.5rem;
  border-bottom: 1px solid var(--line);
}

.mtm-table tr:last-child td {
  border-bottom: none;
}

.mtm-table tr:hover td {
  background: var(--ink2);
}

.mtm-table .sym {
  font-weight: 600;
  color: var(--paper);
}

.tag {
  display: inline-block;
  padding: 2px 6px;
  font-size: 0.58rem;
  border: 1px solid var(--line);
  color: var(--dim);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.tag.buy {
  border-color: var(--cool);
  color: var(--cool);
}

.tag.sell {
  border-color: var(--hot);
  color: var(--hot);
}

.pos {
  color: var(--cool);
}

.neg {
  color: var(--hot);
}

.spark {
  stroke-width: 1.5;
  fill: none;
}

.advisor-grid {
  padding: 0.8rem 0;
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.8rem 1.5rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
}
</style>
